import { useState } from "react";
import { KitSolar, DadosInstalacao } from "@/types";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, Edit, Calculator, Search, RefreshCw, SortAsc, SortDesc } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface KitSolarListProps {
  kits: KitSolar[];
  loading: boolean;
  kitSelecionado: string | null;
  dadosInstalacao: DadosInstalacao;
  onSelecionarKit: (kit: KitSolar) => void;
  onEditarKit: (kit: KitSolar) => void;
  onBuscarKits: () => void;
  onNovoKit: () => void;
}

export const KitSolarList = ({
  kits,
  loading,
  kitSelecionado,
  dadosInstalacao,
  onSelecionarKit,
  onEditarKit,
  onBuscarKits,
  onNovoKit,
}: KitSolarListProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"preco" | "potencia">("preco");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const kitsFiltrados = kits
    .filter((kit) => kit.nome.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      const order = sortOrder === "asc" ? 1 : -1;
      if (sortBy === "preco") {
        return (a.precoTotal - b.precoTotal) * order;
      } else if (sortBy === "potencia") {
        return (a.potenciaSistema - b.potenciaSistema) * order;
      }
      return 0;
    });

  return (
    <div>
      <div className="flex items-center space-x-4">
        <div className="relative w-full max-w-xs">
          <Input
            type="text"
            placeholder="Buscar kit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
        </div>

        <div className="flex items-center space-x-2">
          <Select value={sortBy} onValueChange={(value) => setSortBy(value as "preco" | "potencia")}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="preco">Preço</SelectItem>
              <SelectItem value="potencia">Potência</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
          >
            {sortOrder === "asc" ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-6 w-32 mb-2" />
              <Skeleton className="h-4 w-48 mb-2" />
              <Skeleton className="h-4 w-32 mb-2" />
              <Skeleton className="h-4 w-24" />
            </Card>
          ))}
        </div>
      ) : kits.length === 0 ? (
        <div className="text-center py-10">
          <h3 className="text-lg font-semibold text-gray-700">Nenhum kit encontrado</h3>
          <p className="text-gray-500 mt-2">
            Tente ajustar os filtros ou criar um novo kit.
          </p>
          <div className="mt-4 flex justify-center gap-4">
            <Button onClick={onBuscarKits} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" /> Buscar Novamente
            </Button>
            <Button onClick={onNovoKit}>
              <Calculator className="mr-2 h-4 w-4" /> Criar Novo Kit
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {kitsFiltrados.map((kit) => (
            <Card
              key={kit.id}
              className={`p-4 hover:shadow-md transition-shadow ${
                kitSelecionado === kit.id ? "bg-green-50 border-green-500" : ""
              }`}
            >
              <div className="flex justify-between">
                <h3 className="font-semibold text-lg">{kit.nome}</h3>
                {kitSelecionado === kit.id && (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                )}
              </div>
              
              <div className="space-y-2 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Potência:</span>
                  <span className="font-medium">{kit.potenciaSistema.toFixed(2)} kWp</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Painéis:</span>
                  <span className="font-medium">
                    {kit.paineis[0]?.quantidade || 0} × {kit.paineis[0]?.potencia || 0} kWp
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Inversor:</span>
                  <span className="font-medium">
                    {kit.inversores[0]?.quantidade || 0} × {kit.inversores[0]?.potencia || 0} kW
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Geração Mensal:</span>
                  <span className="font-medium">{kit.geracaoEstimada} kWh</span>
                </div>
                
                {kit.custoTelhado > 0 && dadosInstalacao.tipoTelhado && (
                  <div className="flex justify-between items-center text-amber-700">
                    <span>Custo Telhado ({dadosInstalacao.tipoTelhado}):</span>
                    <span className="font-medium">
                      {kit.custoTelhado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center text-lg">
                  <span className="text-gray-700 font-medium">Preço Total:</span>
                  <span className="font-bold text-green-700">
                    {kit.precoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Payback:</span>
                  <span className="font-medium">{kit.paybackEstimado} meses</span>
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button
                  variant={kitSelecionado === kit.id ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => onSelecionarKit(kit)}
                >
                  {kitSelecionado === kit.id ? "Selecionado" : "Selecionar"}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onEditarKit(kit)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
